import {
  useRef, useState, useEffect, useMemo
} from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { isEmpty, kebabCase, startCase } from "lodash";
import { Button } from "@hydra/atom/components";
import toast from "react-hot-toast";

import { calculateTax, formatDecimalValues, getTestId } from "@/utils/helpers";
import { AlertModal } from "@/components/modals";
import { BoxedContent } from "@/components/common";
import { getTaxRules } from "@/api/finance/taxRuleApi";
import { DynamicFormContainer } from "@/components/dynamic";
import { defaultComponents } from "@/components/dynamic/DynamicFormContainer";
import {
  getDynamicObjectRecords,
  getDynamicObjectRecordById,
  bulkCreateDynamicObjectRecord,
} from "@/api/dynamic/dynamicObjectNameApi";
import { getDynamicObjectByNameWithCamelizedFieldNames } from "@/api/dynamic/dynamicObjectSchemaApi";
import dynamicObjectMap from "@/utils/maps/dynamicObjectMap";
import { useModal, useTaskRedirect } from "@/hooks";
import { TableWithCheckbox } from "@/components/finance/account-receivables";
import {
  getSupplierQuotationTableColumns,
  getMaterialRequestItemDetailTableColumns,
} from "@/components/finance/account-receivables/tableWithCheckboxData";
import showToast from "@/utils/toast/helpers";
import { prepareDynamicObjectData } from "@/utils/dynamic/helpers";
import { getCompany } from "@/api/user/authApi";
import { TASK_TYPES } from "@/components/workflow/constants";

const formatQuotation = (quotation, taxRule) => {
  const { itemType, requestForQuotation } = quotation;
  const { materialRequest } = requestForQuotation;

  const data = {
    key: quotation.id,
    quotation: {
      label: quotation.number,
      value: quotation.id,
    },
    requestForQuotation: {
      label: requestForQuotation.number,
      value: requestForQuotation.id,
    },
    materialRequest: {
      label: materialRequest.number,
      value: materialRequest.id,
    },
    itemDetail: quotation?.itemDetail?.map((item) => {
      const itemData = {
        quantity: item.quantity,
        rate: item.rate,
        amount: item.amount,
        discount: item.discount,
        discountedAmount: item.discountedAmount,
      };
      if (taxRule) {
        itemData.tax = {
          label: taxRule.name,
          value: taxRule.id,
          purchaseRate: taxRule?.purchaseRate,
          saleRate: taxRule?.saleRate,
        };
      }
      const taxAmount =
        (itemData.discountedAmount * (taxRule?.purchaseRate ?? 0)) / 100;
      const totalAmount = itemData.discountedAmount + taxAmount;

      itemData.taxAmount = taxAmount;
      itemData.totalAmount = totalAmount;

      itemData.item = {
        label: item.item?.itemName,
        value: item.item?.id,
      };

      return itemData;
    }),
    discount: quotation.discount,
    discountType: {
      label: startCase(quotation.discountType),
      value: quotation.discountType,
    },
    isSelected: false,
    itemType: {
      label: itemType,
      value: itemType,
    },
  };

  if (quotation?.supplier && JSON.stringify(quotation?.supplier) !== "{}") {
    data.supplier = {
      ...quotation?.supplier,
      label: quotation?.supplier?.name,
      value: quotation?.supplier?.id,
    };
  }
  data.subtotal = data.itemDetail.reduce(
    (prevValue, currValue) => Number(prevValue) + Number(currValue.amount),
    0
  );
  data.discountedSubtotal = data.itemDetail.reduce(
    (prevValue, currValue) =>
      Number(prevValue) + Number(currValue.discountedAmount),
    0
  );
  data.taxAmount = data.itemDetail.reduce(
    (prevValue, currValue) => Number(prevValue) + Number(currValue.taxAmount),
    0
  );

  data.total = data.itemDetail.reduce(
    (prevValue, currValue) => Number(prevValue) + Number(currValue.totalAmount),
    0
  );

  return data;
};

const filterMaterialRequestItemDetail = (materialRequestItemDetail) => {
  const { materialRequest, converted } = materialRequestItemDetail;
  const { status } = materialRequest;

  if (converted === "Converted") {
    return false;
  }

  if (["Partial", "Approved"].includes(status)) {
    return true;
  }

  return false;
};

const formatMaterialRequestItemDetail = (materialRequestItemDetail) => {
  const {
    id: itemDetailid,
    item,
    deliveryLocation,
    unitOfMeasure,
    quantity,
    amount,
    supplier,
    materialRequest,
    convertedQuantity,
    costCenter,
  } = materialRequestItemDetail;

  const remainingQuantity = quantity - Number(convertedQuantity);
  const totalAmount = formatDecimalValues(remainingQuantity * amount);

  return {
    supplier: {
      ...supplier,
      label: supplier.name,
      value: supplier.id,
    },
    materialRequestItemDetailId: itemDetailid,
    item: {
      label: item.itemName,
      value: item.id,
    },
    deliveryLocation: !isEmpty(deliveryLocation) ?
      {
        label: deliveryLocation.name,
        value: deliveryLocation.id,
      } :
      null,
    remainingQuantity,
    quantity: remainingQuantity,
    rate: amount || 0,
    amount: totalAmount,
    discount: 0,
    discountedAmount: totalAmount || 0,
    taxAmount: 0,
    totalAmount,
    unitOfMeasure: unitOfMeasure ?
      {
        label: unitOfMeasure.name,
        value: unitOfMeasure.id,
      } :
      null,
    materialRequest: {
      label: materialRequest.number,
      value: materialRequest.id,
    },
    received: {
      label: "Pending",
      value: "Pending",
    },
    invoiceStatus: {
      label: "Pending",
      value: "Pending",
    },
    costCenter: !isEmpty(costCenter) ?
      {
        lookupObjectName: costCenter.objectName,
        label: costCenter.name,
        value: costCenter.id,
      } :
      null,
    comment: materialRequest.number
  };
};

const formatRfq = (rfq, taxRule) => {
  const { itemType, materialRequest } = rfq;
  const isFixedAsset = itemType === "Asset";

  const data = {
    materialRequest: {
      label: materialRequest.number,
      value: materialRequest.id,
    },
    itemDetail: rfq?.item?.map((item) => {
      const itemData = {
        quantity: item.quantity,
        rate: item.rate || 0,
        amount: item.quantity * item.rate || 0,
        discount: 0,
        discountedAmount: item.quantity * item.rate || 0,
      };
      if (taxRule) {
        itemData.tax = {
          label: taxRule?.name,
          value: taxRule?.id,
          purchaseRate: taxRule?.purchaseRate,
          saleRate: taxRule?.saleRate,
        };
      }

      const taxAmount = (itemData.amount * (taxRule?.purchaseRate ?? 0)) / 100;
      const totalAmount = itemData.amount + taxAmount;

      itemData.taxAmount = taxAmount;
      itemData.totalAmount = totalAmount;

      if (isFixedAsset) {
        itemData.label = item.label;
      } else {
        itemData.item = {
          label: item.item?.itemName,
          value: item.item?.id,
        };
      }

      return itemData;
    }),
    itemType: {
      label: itemType,
      value: itemType,
    },
  };

  data.subtotal = data.itemDetail.reduce(
    (prevValue, currValue) => Number(prevValue) + Number(currValue.amount),
    0
  );

  data.taxAmount = data.itemDetail.reduce(
    (prevValue, currValue) => Number(prevValue) + Number(currValue.taxAmount),
    0
  );

  data.discountedSubtotal = data.subtotal;

  data.total = data.subtotal;

  return data;
};

const quotationInitialState = {
  itemDetail: [],
  subtotal: "",
  discount: "",
  discountType: null,
  total: "",
};

function HeaderRightContent({ openModal, showButton, ...rest }) {
  return (
    <defaultComponents.HeaderRightContent {...rest}>
      {showButton && (
        <Button small bordered onClick={openModal}>
          Select line items
        </Button>
      )}
    </defaultComponents.HeaderRightContent>
  );
}

function PurchaseOrderForm() {
  const { redirect } = useTaskRedirect();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const ref = useRef(null);
  const [state, setState] = useState({});
  const [initialState, setInitialState] = useState(null);
  const [quotationTableData, setQuotationTableData] = useState([]);
  const [
    materialRequestItemDetailTableData,
    setMaterialRequestItemDetailTableData,
  ] = useState([]);
  const { isOpen, closeModal, openModal } = useModal(false);
  const [searchParams] = useSearchParams();

  const requestForQuotation = searchParams.get("requestForQuotation");
  const materialRequest = searchParams.get("materialRequest");
  const taskId = searchParams.get("taskId");
  const taskType = searchParams.get("taskType");
  const isCreatingPOfromRFQ = Boolean(requestForQuotation);
  const { id } = useParams();
  const isEditing = Boolean(id);

  const { data: objectSchema } = useQuery(
    [
      "dynamic-object-camelized-schema",
      dynamicObjectMap.get("PurchaseOrderItemDetailObjectName"),
    ],
    () =>
      getDynamicObjectByNameWithCamelizedFieldNames(
        dynamicObjectMap.get("PurchaseOrderItemDetailObjectName")
      ),
    {
      enabled: isEditing,
    }
  );

  const saveBulkMutation = useMutation(({ objectName, dataObjects }) =>
    bulkCreateDynamicObjectRecord(objectName, dataObjects)
  );

  const queryFilters = useMemo(() => {
    const filters = {
      supplier: state?.supplier?.value,
      queryMode: "Deep",
    };

    if (materialRequest) {
      filters.detailId = materialRequest;
    }

    return filters;
  }, [materialRequest, state?.supplier]);

  const { data: materialRequestItemDetailsData } = useQuery(
    [
      kebabCase(dynamicObjectMap.get("MaterialRequestItemDetailObjectName")),
      {
        supplier: state?.supplier?.value,
      },
    ],
    () =>
      getDynamicObjectRecords(
        kebabCase(dynamicObjectMap.get("MaterialRequestItemDetailObjectName")),
        queryFilters
      ),
    {
      enabled: Boolean(state?.supplier?.value && !isCreatingPOfromRFQ),
    }
  );

  useEffect(() => {
    if (materialRequestItemDetailsData) {
      const formattedMaterialRequestItemDetail =
        materialRequestItemDetailsData.data
          .filter(filterMaterialRequestItemDetail)
          .map((m) => formatMaterialRequestItemDetail(m));
      setMaterialRequestItemDetailTableData(formattedMaterialRequestItemDetail);
      openModal();
    }
  }, [materialRequestItemDetailsData]);

  const { data: rfqData } = useQuery(
    [
      kebabCase(dynamicObjectMap.get("RequestForQuotationObjectName")),
      requestForQuotation,
    ],
    () =>
      getDynamicObjectRecordById(
        kebabCase(dynamicObjectMap.get("RequestForQuotationObjectName")),
        requestForQuotation
      ),
    {
      enabled: Boolean(requestForQuotation && !taskId),
    }
  );

  const { data: rfqQuotationsData } = useQuery(
    [
      kebabCase(dynamicObjectMap.get("QuotationObjectName")),
      state?.supplier?.value,
    ],
    () =>
      getDynamicObjectRecords(
        kebabCase(dynamicObjectMap.get("QuotationObjectName")),
        {
          requestForQuotation,
          isApproved: true,
          queryMode: "Deep",
        }
      ),
    {
      enabled: Boolean(requestForQuotation),
    }
  );

  const { data: quotationsData } = useQuery(
    [
      kebabCase(dynamicObjectMap.get("QuotationObjectName")),
      state?.supplier?.value,
    ],
    () =>
      getDynamicObjectRecords(
        kebabCase(dynamicObjectMap.get("QuotationObjectName")),
        {
          supplier: state?.supplier?.value,
          queryMode: "Deep",
        }
      ),
    {
      enabled: Boolean(state?.supplier?.value && !requestForQuotation),
    }
  );

  const { data: taxRulesData } = useQuery(["tax-rule"], getTaxRules);

  useEffect(() => {
    if (rfqData && taxRulesData?.data) {
      const taxRule = taxRulesData?.data?.find(
        (item) =>
          item.name ===
          "Input VAT Partial-recoverable - Residential & Admin (5%)"
      );
      const formattedRfq = formatRfq(rfqData, taxRule);
      setInitialState(formattedRfq);
    }
  }, [rfqData, taxRulesData]);

  useEffect(() => {
    if (
      rfqQuotationsData?.data &&
      rfqQuotationsData?.data?.length &&
      taxRulesData?.data
    ) {
      const taxRule = taxRulesData?.data?.find(
        (item) =>
          item.name ===
          "Input VAT Partial-recoverable - Residential & Admin (5%)"
      );
      const formattedQuotation = formatQuotation(
        rfqQuotationsData.data[0],
        taxRule
      );
      setInitialState(formattedQuotation);
    }
  }, [rfqQuotationsData, taxRulesData]);

  useEffect(() => {
    if (
      quotationsData?.data &&
      quotationsData?.data?.length &&
      state?.supplier &&
      taxRulesData?.data
    ) {
      const taxRule = taxRulesData?.data?.find(
        (item) =>
          item.name ===
          "Input VAT Partial-recoverable - Residential & Admin (5%)"
      );
      const formattedQuotations = quotationsData?.data?.map((r) =>
        formatQuotation(r, taxRule)
      );
      setQuotationTableData(formattedQuotations);
      openModal();
    }
  }, [quotationsData, state?.supplier, taxRulesData]);

  const prepareLineItem = (key, value, lineItem) => {
    const formState = ref.current.getState();
    formState[key] = value;

    const {
      discountType,
      discount: parentDiscount,
      applyDiscountIndividually,
      subtotal,
      supplier
    } = formState;
    let discount = parentDiscount;
    let discountAmount = 0;
    const discountRatio = parentDiscount / Number(subtotal);

    let selectedTax = null;

    if (supplier) {
      const { tax } = supplier;

      if (tax) {
        selectedTax = {
          ...tax,
          label: tax.name,
          value: tax.id
        };
      }
    }

    const {
      tax,
      quantity: addedQuantity,
      rate,
      remainingQuantity,
      discount: lineItemDiscount,
    } = lineItem;
    let quantity = addedQuantity;

    if (remainingQuantity && quantity > remainingQuantity) {
      quantity = remainingQuantity;
    }

    const amount = quantity * (rate || 0);

    if (applyDiscountIndividually) {
      discount = lineItemDiscount ?? 0;
      discountAmount = lineItemDiscount;
    }

    if (discountType.value === "Amount" && !applyDiscountIndividually) {
      discountAmount = formatDecimalValues(discountRatio * amount);
    }

    if (discountType?.value === "Percentage" && !applyDiscountIndividually) {
      discountAmount = formatDecimalValues(amount * (discount / 100));
    }

    if (discountAmount > amount) {
      discountAmount = amount;
    }

    if (tax) {
      selectedTax = tax;
    }

    let discountedAmount = amount - Number(discountAmount || 0);
    let taxAmount = 0;
    if (selectedTax) {
      const parameters = {
        amount: discountedAmount,
        amountOfTax: { value: "Exclusive" },
        tax: selectedTax,
        taxAmount: "",
      };
      const {
        taxAmount: calculatedTaxAmount,
        principalAmount: discountedPrincipalAmount,
      } = calculateTax(parameters);
      taxAmount = calculatedTaxAmount;
      discountedAmount = discountedPrincipalAmount;
    }

    const totalAmount = formatDecimalValues(
      Number(discountedAmount) + Number(taxAmount)
    );

    return {
      ...lineItem,
      quantity,
      discount: discountAmount,
      amount,
      discountedAmount,
      tax: selectedTax,
      taxAmount,
      totalAmount,
    };
  };

  const setTaxAndTotal = (key, value) => {
    const formState = ref.current.getState();
    formState[key] = value;

    const {
      itemDetail, subtotal, discountType, discount
    } = formState;
    let parentDiscount = discount;

    if (discountType?.value === "Amount") {
      if (discount > subtotal) {
        parentDiscount = subtotal;
      }
    }

    if (discountType?.value === "Percentage") {
      if (discount > 100) {
        parentDiscount = 100;
      }
    }

    if (key === "itemDetailTable") {
      const lineItem = prepareLineItem(key, value, value);
      ref.current.setFormValue("itemDetailTable", lineItem);
    } else {
      const updatedLineItem = itemDetail.map((item) =>
        prepareLineItem(key, value, item)
      );
      const subtotalAmount = updatedLineItem.reduce(
        (prevValue, currentValue) =>
          Number(prevValue) + Number(currentValue.amount),
        0
      );
      const discountedSubtotal = updatedLineItem.reduce(
        (prevValue, currentValue) =>
          Number(prevValue) + Number(currentValue.discountedAmount),
        0
      );
      const taxAmount = updatedLineItem.reduce(
        (prevValue, currentValue) =>
          Number(prevValue) + Number(currentValue.taxAmount),
        0
      );
      const totalAmount = updatedLineItem.reduce(
        (prevValue, currentValue) =>
          Number(prevValue) + Number(currentValue.totalAmount),
        0
      );

      const saving = Number(subtotalAmount) - Number(discountedSubtotal);

      ref.current.setFormState({
        discount: parentDiscount,
        itemDetail: updatedLineItem,
        subtotal: subtotalAmount,
        discountedSubtotal,
        taxAmount,
        total: totalAmount,
        saving,
      });
    }
  };

  const addLineItems = async (itemDetails) => {
    const formState = ref.current.getState();
    const { itemDetail } = formState;
    const existingMaterialRequestLineItemIds = itemDetail.map(
      (t) => t.materialRequestItemDetailId
    );

    let newItemDetails = itemDetails.filter(
      (t) =>
        t.isSelected &&
        !existingMaterialRequestLineItemIds.includes(
          t.materialRequestItemDetailId
        )
    );

    if (newItemDetails.length) {
      const toastId = toast.loading("Adding line items...");

      const dataObjects = newItemDetails.map((t) => {
        const recordData = prepareDynamicObjectData(
          objectSchema?.document,
          t,
          isEditing
        );

        recordData.detailId = id;
        recordData.company = getCompany();

        return recordData;
      });

      try {
        const response = await saveBulkMutation.mutateAsync({
          objectName: dynamicObjectMap.get("PurchaseOrderItemDetailObjectName"),
          dataObjects,
        });

        newItemDetails = newItemDetails.map((t, i) => {
          t.id = response.data[i].id;

          return t;
        });
      } catch (error) {
        toast.error("Could not add line items. Try again!", {
          id: toastId,
        });
      }

      toast.success("Line items added!", {
        id: toastId,
      });

      queryClient.invalidateQueries({
        queryKey: [
          kebabCase(dynamicObjectMap.get("PurchaseOrderItemDetailObjectName")),
        ],
      });

      queryClient.invalidateQueries({
        queryKey: [
          kebabCase(
            dynamicObjectMap.get("MaterialRequestItemDetailObjectName")
          ),
        ],
      });

      newItemDetails = newItemDetails.concat(itemDetail);
      setTaxAndTotal("itemDetail", newItemDetails);
    }
  };

  const setSupplierState = (value) => {
    const data = {
      paymentTerm: null,
      tax: null
    };

    if (!value) {
      ref.current.setFormState(data);
      return;
    }

    const { paymentTerm, tax } = value;

    if (paymentTerm) {
      data.paymentTerm = {
        label: paymentTerm.name,
        value: paymentTerm.id
      };
    }

    if (tax) {
      data.tax = {
        ...tax,
        label: tax.name,
        value: tax.id
      };
    }

    ref.current.setFormState(data);
  };

  const onStateChange = (key, value) => {
    if (key === "supplier") {
      setState({
        ...state,
        [key]: value,
      });

      setSupplierState(value);

      if (!value) {
        ref.current.setFormState(quotationInitialState);
      }
    }

    if (
      [
        "itemDetailTable",
        "itemDetail",
        "discountType",
        "discount",
        "applyDiscountIndividually",
      ].includes(key)
    ) {
      setTaxAndTotal(key, value);
    }
  };

  const handleClose = () => {
    closeModal();
  };

  const handleConfirm = () => {
    if (quotationTableData.length) {
      const selectedQuotation = quotationTableData.find((q) => q.isSelected);

      if (selectedQuotation) {
        ref.current.setFormState(selectedQuotation);
      }
    }

    if (materialRequestItemDetailTableData.length && !isEditing) {
      const selectedMaterialRequestItemDetail =
        materialRequestItemDetailTableData.filter((q) => q.isSelected);

      if (selectedMaterialRequestItemDetail.length) {
        const formState = ref.current.getState();
        const { itemDetail } = formState;

        const data = {
          itemDetail: [],
        };

        if (itemDetail && itemDetail.length) {
          const existingMaterialRequestItemDetailIds = itemDetail.map(
            (s) => s.materialRequestItemDetailId
          );

          const filteredMaterialRequestItemDetail =
            selectedMaterialRequestItemDetail.filter(
              (s) =>
                !existingMaterialRequestItemDetailIds.includes(
                  s.materialRequestItemDetailId
                )
            );

          data.itemDetail = [
            ...itemDetail,
            ...filteredMaterialRequestItemDetail,
          ];
        } else {
          data.itemDetail = selectedMaterialRequestItemDetail;
        }

        ref.current.setFormState(data);
        setTaxAndTotal("itemDetail", data.itemDetail);
      }
    }

    if (materialRequestItemDetailTableData.length && isEditing) {
      addLineItems(materialRequestItemDetailTableData);
    }

    handleClose();
  };

  const onBeforeSave = () => {
    const formState = ref.current.getState();
    const {
      discountType, discount, subtotal, itemDetail
    } = formState;

    const lineItemsWithZeroTotal = itemDetail.filter(
      (o) => Number(o.totalAmount || 0) === 0
    );

    if (lineItemsWithZeroTotal.length) {
      showToast(
        `Total amount for one or more line items is 0.
         Please enter a larger number or remove the line item from table.`
      );
      return false;
    }

    if (discountType?.value === "Percentage") {
      if (discount >= 100) {
        showToast("A purchase order cannot be fully discounted", "error");
        return false;
      }
    }

    if (discount >= subtotal) {
      showToast("A purchase order cannot be fully discounted", "error");
      return false;
    }

    return true;
  };

  const getReadOnlyFields = () => {
    if (isCreatingPOfromRFQ) {
      return ["supplier"];
    }

    return [];
  };

  const submitForm = () => {
    const submitButton = document.getElementById(
      `dynamic-form-submit-button-${kebabCase(
        dynamicObjectMap.get("PurchaseOrderObjectName")
      )}`
    );
    submitButton.click();
  };

  const handleSave = () => {
    const formState = ref.current.getState();
    formState.status = {
      label: "Draft",
      value: "Draft",
    };

    const { applyDiscountIndividually, itemDetail } = formState;

    if (applyDiscountIndividually) {
      formState.discountType = {
        label: "Amount",
        value: "Amount",
      };

      formState.discount = itemDetail.reduce(
        (prevValue, currValue) =>
          Number(prevValue) + Number(currValue.discount),
        0
      );
    }

    submitForm();
  };

  const handleSubmit = () => {
    const formState = ref.current.getState();
    formState.status = {
      label: "In Progress",
      value: "InProgress"
    };
    formState.transactionDate = new Date();

    const { applyDiscountIndividually, itemDetail } = formState;

    if (applyDiscountIndividually) {
      formState.discountType = {
        label: "Amount",
        value: "Amount",
      };

      formState.discount = itemDetail.reduce(
        (prevValue, currValue) =>
          Number(prevValue) + Number(currValue.discount),
        0
      );
    }

    submitForm();
  };

  const onSuccess = (recordId) => {
    if (taskId && taskType === TASK_TYPES.UPDATE) {
      redirect(-1, {
        recordId,
        success: true,
      });
      return;
    }

    if (taskId) {
      navigate("/finance/tasks");
      return;
    }

    navigate(-1);
  };

  return (
    <BoxedContent>
      <AlertModal
        icon="file-check-stroke-icon"
        iconClass="success"
        title="Select line items"
        subtitle="Add line items to the purchase order"
        onClose={handleClose}
        isOpen={isOpen}
        onConfirm={handleConfirm}
        size="large"
      >
        {quotationTableData.length ? (
          <TableWithCheckbox
            data={quotationTableData}
            columns={getSupplierQuotationTableColumns()}
            setData={setQuotationTableData}
          />
        ) : null}
        {materialRequestItemDetailTableData.length ? (
          <TableWithCheckbox
            data={materialRequestItemDetailTableData}
            columns={getMaterialRequestItemDetailTableColumns()}
            setData={setMaterialRequestItemDetailTableData}
            allowMultiple
          />
        ) : null}
      </AlertModal>
      <DynamicFormContainer
        ref={ref}
        initialData={initialState}
        objectName={dynamicObjectMap.get("PurchaseOrderObjectName")}
        showHeader
        showLinkedViews
        setIsLoading={(loading) =>
          setState((prevState) => ({
            ...prevState,
            isLoading: loading,
          }))}
        onStateChange={onStateChange}
        onSuccess={onSuccess}
        navigate={false}
        showButtons={false}
        readOnlyFields={getReadOnlyFields()}
        onBeforeSave={onBeforeSave}
        components={{
          HeaderRightContent: (props) =>
            HeaderRightContent({
              openModal,
              showButton: Boolean(
                materialRequestItemDetailTableData.length ||
                  quotationTableData.length
              ),
              ...props,
            }),
        }}
      />
      <div className="buttons-container buttons-at-end">
        <Button
          small
          bordered
          onClick={() => navigate(-1)}
          testId={getTestId(
            `${dynamicObjectMap.get("PurchaseOrderObjectName")}-Submit-Button`
          )}
        >
          Cancel
        </Button>
        {
          !taskId && (
            <Button
              small
              onClick={handleSave}
              loading={state.isLoading}
              testId={getTestId(
                `${dynamicObjectMap.get("PurchaseOrderObjectName")}-Submit-Button`
              )}
            >
              Save
            </Button>
          )
        }
        {
          !taskId && (
            <Button
              small
              onClick={handleSubmit}
              loading={state.isLoading}
              testId={getTestId(
                `${dynamicObjectMap.get("PurchaseOrderObjectName")}-Submit-Button`
              )}
            >
              Submit
            </Button>
          )
        }

        {
          taskId && (
            <Button
              small
              onClick={submitForm}
              loading={state.isLoading}
              testId={getTestId(
                `${dynamicObjectMap.get("PurchaseOrderObjectName")}-Submit-Button`
              )}
            >
              Update
            </Button>
          )
        }
      </div>
    </BoxedContent>
  );
}

export default PurchaseOrderForm;
